import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, ChevronLeft, Loader2, Pause, Play } from 'lucide-react';
import { useSettings } from '@/lib/store';
import { chapter } from '@/features/quran/api';
import { usePlayer } from '@/features/player/audioEngine';
import { reciterById } from '@/data/reciters';
import { RUQYAH_DUAS, RUQYAH_GUIDANCE, RUQYAH_VERSES, type RuqyahDua, type RuqyahVerses } from '@/data/ruqyah';
import { IconButton, Segmented, haptic } from '@/components/ui';
import { RuqyahListen } from './RuqyahListen';
import { AdhkarArabic, AdhkarDisplayButton } from './AdhkarText';

type Tab = 'listen' | 'quran' | 'duas' | 'how';
const keysOf = (v: RuqyahVerses) => Array.from({ length: v.to - v.from + 1 }, (_, i) => `${v.surah}:${v.from + i}`);
const label = (v: RuqyahVerses) => `${chapter(v.surah).n} ${v.from === v.to ? v.from : `${v.from}–${v.to}`}`;

export function RuqyahPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('listen');
  const [showTr, setShowTr] = useState(true);
  return (
    <div className="page-pad !pt-0">
      <header className="glass sticky top-0 z-30 px-3 pb-3" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <div className="flex items-center gap-2">
          <IconButton label="Back" onClick={() => nav('/adhkar')}><ChevronLeft size={20} /></IconButton>
          <div className="flex-1 text-center">
            <div className="text-[16px] font-bold">Ruqyah</div>
            <div className="font-kufi text-[14px] text-gold">الرقية الشرعية</div>
          </div>
          <AdhkarDisplayButton showTr={showTr} setShowTr={setShowTr} />
        </div>
        <div className="mt-2"><Segmented label="Section" value={tab} onChange={setTab} options={[{ value: 'listen', label: 'Listen' }, { value: 'quran', label: 'Read' }, { value: 'duas', label: 'Duʿāʾ' }, { value: 'how', label: 'How to' }]} /></div>
      </header>

      <div className="mx-auto max-w-[720px] space-y-3 px-4 pt-4">
        {tab === 'listen' && <RuqyahListen />}
        {tab === 'quran' && <>
          <p className="px-1 text-[13px] leading-relaxed text-muted">Recite over yourself or the one who is ill, then blow lightly. <span className="font-semibold text-accent">Sunnah</span> marks passages reported from the Prophet ﷺ and his Companions; the others are commonly recited by scholars in ruqyah.</p>
          {RUQYAH_VERSES.map((v) => <VersesCard key={v.id} v={v} showTr={showTr} />)}
        </>}
        {tab === 'duas' && RUQYAH_DUAS.map((d) => <DuaCard key={d.id} d={d} showTr={showTr} />)}
        {tab === 'how' && RUQYAH_GUIDANCE.map((g) => (
          <div key={g.title} className="rounded-[22px] bg-surface p-5">
            <h3 className="text-[16px] font-semibold">{g.title}</h3>
            <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{g.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function VersesCard({ v, showTr }: { v: RuqyahVerses; showTr: boolean }) {
  const reciterId = useSettings((s) => s.settings.reciterId);
  const p = usePlayer();
  // A passage plays as an āyah range, so it's this card's audio when the range matches.
  const mine = p.surah === v.surah && p.range?.from === v.from && p.range?.to === v.to;
  const playing = mine && (p.status === 'playing' || p.status === 'gap');
  const loading = mine && p.status === 'loading';
  const reciter = reciterById(p.reciterId || reciterId);
  return (
    <div className="rounded-[22px] bg-surface p-5">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-snug">{v.title}</h3>
          <div className="text-[12px] text-muted">{label(v)}{v.sunnah && <span className="ml-2 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent">Sunnah</span>}</div>
        </div>
        <button type="button" aria-label={playing ? `Pause ${v.title}` : `Play ${v.title}`} className="press grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-accent-ink"
          onClick={() => { haptic(); if (mine && p.track) p.toggle(); else void p.play(v.surah, { fromAyah: v.from, toAyah: v.to }); }}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
      </div>
      <AdhkarArabic ar="" quran={keysOf(v)} />
      {showTr && <p className="mt-3 text-[14px] leading-relaxed text-muted">{v.why}</p>}
      {mine && <p className="mt-2 text-[12px] text-faint">Recited by {reciter.name}</p>}
    </div>
  );
}

function DuaCard({ d, showTr }: { d: RuqyahDua; showTr: boolean }) {
  const [c, setC] = useState(0);
  const done = c >= d.count;
  return (
    <div role="button" tabIndex={0} aria-label={`Count ${d.title}`} onClick={() => { if (!done) { haptic(c + 1 === d.count ? 30 : 8); setC(c + 1); } }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !done) { e.preventDefault(); setC(c + 1); } }}
      className={`press cursor-pointer rounded-[22px] p-5 ${done ? 'bg-accent/10' : 'bg-surface'}`}>
      <h3 className="mb-2 text-[13px] font-semibold leading-snug text-gold">{d.title}</h3>
      <AdhkarArabic ar={d.ar} />
      {showTr && <>
        <p className="mt-3 text-[14px] italic leading-relaxed text-muted">{d.tr}</p>
        <p className="mt-2 text-[15px] leading-relaxed">{d.en}</p>
        {d.note && <p className="mt-2 rounded-2xl bg-fill p-3 text-[13px] leading-relaxed text-muted">{d.note}</p>}
      </>}
      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="min-w-0 text-[12px] text-muted">{d.source}</span>
        <span className={`flex h-12 min-w-12 shrink-0 items-center justify-center rounded-full px-3 text-[17px] font-bold tabular-nums ${done ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>
          {done ? <CheckCircle2 size={22} /> : `${c}/${d.count}`}
        </span>
      </div>
    </div>
  );
}
